import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { SubscriptionActivationConfirmation, SubscriptionActivationResponse, SubscriptionActivationResponseSchema } from '../schemas'

export function getSubscriptionActivation(): Promise<SubscriptionActivationResponse> {
  return requestSubscriptionActivation('GET')
}

export function postSubscriptionActivation(confirmation: SubscriptionActivationConfirmation, checkOnly = false): Promise<SubscriptionActivationResponse> {
  return requestSubscriptionActivation('POST', confirmation, checkOnly)
}

async function requestSubscriptionActivation(
  method: 'GET' | 'POST',
  confirmation?: SubscriptionActivationConfirmation,
  checkOnly = false
): Promise<SubscriptionActivationResponse> {
  const transactionId = nanoid()
  const url = `${kPaxtonAppApiRESTBaseUrl()}/api/v1/payments/subscription-activation${checkOnly ? '?check_only=true' : ''}`
  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error('User not authenticated')
    const response = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'X-Transaction-Id': transactionId },
      ...(confirmation ? { body: JSON.stringify(confirmation) } : {}),
    })
    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const detail =
        !parseError && data && typeof data === 'object' && 'detail' in data && typeof data.detail === 'string'
          ? data.detail
          : 'Billing is temporarily unavailable. Please try again.'
      throw new ApiError(response.status, detail)
    }
    const parsed = parseError ? null : SubscriptionActivationResponseSchema.safeParse(data)
    if (parseError || !parsed?.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: transactionId })
        Sentry.captureException(new Error('Subscription activation response validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            parseError: parseError ? formatUnknownError(parseError) : null,
            responseKeys: getTopLevelKeys(data),
            issues: parsed && !parsed.success ? parsed.error.issues : null,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid subscription activation response')
    }
    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) Sentry.captureException(error, { extra: { requestUrl: url, transactionId } })
    throw error
  }
}
